import React, { createContext, useEffect, useState } from "react";

import { GetEmbeddedSubtitlesLookupResponseItem } from "../../api/admin-api/EmbeddedSubtitlesResponse";
import { useCachedAdminApi } from "../../api/admin-api/useCachedAdminApi";
import { Language } from "../../types/Language";

type EmbeddedLanguage = GetEmbeddedSubtitlesLookupResponseItem;

interface ILanguagesDataContext {
    languages: Language[];
    dubbingLanguages: Language[];
    subtitleLanguages: Language[];
    embeddedSubtitleLanguages: EmbeddedLanguage[];
    isLoading: boolean;
}

const defaultLanguagesDataContext: ILanguagesDataContext = {
    languages: [],
    dubbingLanguages: [],
    subtitleLanguages: [],
    embeddedSubtitleLanguages: [],
    isLoading: true
};

export const LanguagesDataContext = createContext<ILanguagesDataContext>(defaultLanguagesDataContext);

type LanguagesDataProviderProps = React.PropsWithChildren<unknown>;

export const LanguagesDataProvider: React.FunctionComponent<LanguagesDataProviderProps> = ({
    children
}: LanguagesDataProviderProps) => {
    const [languages, setLanguages] = useState<Language[]>([]);
    const [dubbings, setDubbings] = useState<Language[]>([]);
    const [dynamicSubtitles, setDynamicSubtitles] = useState<Language[]>([]);
    const [embeddedSubtitles, setEmbeddedSubtitles] = useState<EmbeddedLanguage[]>([]);

    const [areLanguagesLoading, setAreLanguagesLoading] = useState<boolean>(true);
    const [areDynamicSubtitlesLoading, setAreDynamicSubtitlesLoading] = useState(false);
    const [areEmbeddedSubtitlesLoading, setAreEmbeddedSubitltesLoading] = useState(false);
    const [areDubbingsLoading, setAreDubbingsLoading] = useState(false);

    const { getDynamicSubtitles, getEmbeddedSubtitles, getDubbings, getLanguagesLookup } = useCachedAdminApi();

    useEffect(() => {
        initializeLanguages();
    }, []);

    const initializeLanguages = async (): Promise<void> => {
        setAreLanguagesLoading(true);

        getLanguagesLookup()
            .then((response) => {
                setLanguages(response.items);
            })
            .finally(() => setAreLanguagesLoading(false));

        setAreDynamicSubtitlesLoading(true);

        getDynamicSubtitles()
            .then((response) => {
                setDynamicSubtitles(response.items);
            })
            .finally(() => setAreDynamicSubtitlesLoading(false));

        setAreEmbeddedSubitltesLoading(true);
        getEmbeddedSubtitles()
            .then((response) => {
                setEmbeddedSubtitles(response.items);
            })
            .finally(() => setAreEmbeddedSubitltesLoading(false));

        setAreDubbingsLoading(true);
        getDubbings()
            .then((response) => {
                setDubbings(response.items);
            })
            .finally(() => setAreDubbingsLoading(false));
    };

    const isLoading =
        areLanguagesLoading || areEmbeddedSubtitlesLoading || areDynamicSubtitlesLoading || areDubbingsLoading;

    return (
        <LanguagesDataContext.Provider
            value={{
                languages,
                dubbingLanguages: dubbings,
                subtitleLanguages: dynamicSubtitles,
                isLoading,
                embeddedSubtitleLanguages: embeddedSubtitles
            }}
        >
            {children}
        </LanguagesDataContext.Provider>
    );
};
